import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COUNTRIES,
  CURRENCIES,
  LEDGER_PARTY_ROLES,
  RoleKey,
  VENDOR_ADVANCE_MODES,
  formatBasisPoints,
  roleLabel,
  schemas,
  type BankAccount,
  type LedgerPartyRole,
  type Vendor,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useMoney } from '@/hooks/useMoney';
import { describeError } from '@/lib/errors';
import { formatDate, humanize, rupeesToMinor } from '@/lib/format';
import { Modal, Money, StatusBadge } from '@/components/ui';
import { useExpenseLedgers } from '@/components/ExpenseLedgerOptions';
import { VendorAdvancePanel } from '@/components/VendorAdvancePanel';
import { VendorLedgersPanel, suggestedVendorAccount } from '@/components/VendorLedgersPanel';
import { CrudPage, type CrudColumn, type CrudRowAction, type FieldDefinition } from './CrudPage';

export function CompaniesPage() {
  const { data: groups } = useQuery({
    queryKey: ['groups', 'for-companies'],
    queryFn: () => api.settings.groups({ pageSize: 200 }),
  });
  const groupName = (id: string | undefined) =>
    groups?.items.find((group) => group.id === id)?.name;

  return (
    <CrudPage
      title="Companies"
      subtitle="Legal entities whose payments this platform handles"
      queryKey="settings-companies"
      permissions={{
        read: 'company:read',
        create: 'company:create',
        update: 'company:update',
      }}
      list={(query) => api.settings.companies(query)}
      create={(body) => api.settings.createCompany(body)}
      update={(id, body) => api.settings.updateCompany(id, body)}
      schemas={{ create: schemas.createCompanyRequest, update: schemas.updateCompanyRequest }}
      restore={(id) => api.settings.updateCompany(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Legal name', render: (row) => row.legalName ?? '—' },
        { header: 'Group', render: (row) => groupName(row.groupId) ?? '—' },
        {
          header: 'Country · currency',
          render: (row) => `${row.country ?? 'IN'} · ${row.baseCurrency ?? 'INR'}`,
        },
        {
          header: 'GSTIN',
          render: (row) => <span className="font-mono text-xs">{row.gstin ?? '—'}</span>,
        },
        {
          header: 'Invoice inbox',
          render: (row) =>
            row.invoiceInboxAddress ?? <span className="text-slate-400">Not configured</span>,
        },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'legalName', label: 'Legal name' },
        {
          name: 'groupId',
          label: 'Group',
          type: 'select',
          options: (groups?.items ?? [])
            .filter((group) => group.active)
            .map((group) => ({ value: group.id, label: group.name })),
          help: 'Consolidation only — the company stays the boundary for books and GST.',
        },
        {
          name: 'country',
          label: 'Country',
          type: 'select',
          options: COUNTRIES.map((country) => ({ value: country.code, label: country.name })),
          help: 'GSTIN, PAN, IFSC and TDS apply to companies in India only.',
        },
        {
          name: 'baseCurrency',
          label: 'Currency',
          type: 'select',
          options: CURRENCIES.map((currency) => ({ value: currency, label: currency })),
          help: 'Every invoice, payment and statement of this company is in this currency. Defaults to the country’s; India is always INR. Locked once the company has invoices.',
        },
        {
          name: 'gstin',
          label: 'GSTIN',
          help: 'Indian companies only. Unique within the tenant: one legal entity per GSTIN.',
        },
        { name: 'cin', label: 'CIN' },
        {
          name: 'invoiceInboxAddress',
          label: 'Shared invoice mailbox',
          type: 'email',
          help: 'A mailbox the platform monitors for everyone. To pull invoices from your own Outlook instead, use Invoice Mailbox under Operations.',
        },
      ]}
    />
  );
}

/**
 * Groups — the only organisation level above the legal entity, so unlike every
 * other master it carries no company.
 */
export function GroupsPage() {
  return (
    <CrudPage
      title="Groups"
      subtitle="Sits above the legal entity, so a CFO can see the whole group at once"
      queryKey="settings-groups"
      permissions={{
        read: 'group:read',
        create: 'group:create',
        update: 'group:update',
        delete: 'group:delete',
      }}
      list={(query) => api.settings.groups(query)}
      create={(body) => api.settings.createGroup(body)}
      update={(id, body) => api.settings.updateGroup(id, body)}
      schemas={{ create: schemas.createGroupRequest, update: schemas.updateGroupRequest }}
      remove={(id) => api.settings.deleteGroup(id)}
      restore={(id) => api.settings.updateGroup(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Code', render: (row) => <span className="font-mono text-xs">{row.code}</span> },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'code', label: 'Code', required: true },
      ]}
    />
  );
}

export function RegionsPage() {
  return (
    <CrudPage
      title="Regions"
      subtitle="Geographic grouping used to filter invoices, payments and reports"
      queryKey="settings-regions"
      permissions={{
        read: 'region:read',
        create: 'region:create',
        update: 'region:update',
        delete: 'region:delete',
      }}
      list={(query) => api.settings.regions(query)}
      create={(body) => api.settings.createRegion(body)}
      update={(id, body) => api.settings.updateRegion(id, body)}
      schemas={{ create: schemas.createRegionRequest, update: schemas.updateRegionRequest }}
      remove={(id) => api.settings.deleteRegion(id)}
      restore={(id) => api.settings.updateRegion(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Code', render: (row) => <span className="font-mono text-xs">{row.code}</span> },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'code', label: 'Code', required: true },
      ]}
    />
  );
}

/**
 * Verticals — the first rung of the revised approval ladder.
 *
 * The head is load-bearing in the same way a department head is: VERTICAL_HEAD
 * approval steps resolve through it.
 */
export function VerticalsPage() {
  const { companyId } = useAuth();

  const { data: users } = useQuery({
    queryKey: ['users', 'for-verticals', companyId],
    queryFn: () => api.settings.users({ companyId, pageSize: 200 }),
  });

  const userName = (id: string | undefined) => users?.items.find((user) => user.id === id)?.name;

  return (
    <CrudPage
      title="Verticals"
      subtitle="Business lines. Approval routes through the vertical head before finance."
      queryKey="settings-verticals"
      permissions={{
        read: 'vertical:read',
        create: 'vertical:create',
        update: 'vertical:update',
        delete: 'vertical:delete',
      }}
      list={(query) => api.settings.verticals(query)}
      create={(body) => api.settings.createVertical(body)}
      update={(id, body) => api.settings.updateVertical(id, body)}
      schemas={{ create: schemas.createVerticalRequest, update: schemas.updateVerticalRequest }}
      remove={(id) => api.settings.deleteVertical(id)}
      restore={(id) => api.settings.updateVertical(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Code', render: (row) => <span className="font-mono text-xs">{row.code}</span> },
        {
          header: 'Head',
          render: (row) =>
            row.headUserId ? (
              (userName(row.headUserId) ?? 'Unknown user')
            ) : (
              <span className="text-amber-700">
                Not set — approvals fall back to anyone holding Vertical Head
              </span>
            ),
        },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'code', label: 'Code', required: true },
        {
          name: 'headUserId',
          label: 'Vertical head',
          type: 'select',
          options: (users?.items ?? []).map((user) => ({ value: user.id, label: user.name })),
          help: 'Approval rules with a "Vertical head" step route to this person.',
        },
      ]}
      toFormValues={(row) => ({ name: row.name, code: row.code, headUserId: row.headUserId ?? '' })}
    />
  );
}

export function BusinessUnitsPage() {
  const { companyId } = useAuth();

  const { data: verticals } = useQuery({
    queryKey: ['verticals', 'for-business-units', companyId],
    queryFn: () => api.settings.verticals({ companyId, pageSize: 200 }),
  });

  const verticalName = (id: string | undefined) =>
    verticals?.items.find((vertical) => vertical.id === id)?.name;

  return (
    <CrudPage
      title="Business units"
      subtitle="Units within one or more verticals. A classified unit is visible only to users granted it by name."
      queryKey="settings-business-units"
      permissions={{
        read: 'business_unit:read',
        create: 'business_unit:create',
        update: 'business_unit:update',
        delete: 'business_unit:delete',
      }}
      list={(query) => api.settings.businessUnits(query)}
      create={(body) => api.settings.createBusinessUnit(body)}
      update={(id, body) => api.settings.updateBusinessUnit(id, body)}
      schemas={{
        create: schemas.createBusinessUnitRequest,
        update: schemas.updateBusinessUnitRequest,
      }}
      remove={(id) => api.settings.deleteBusinessUnit(id)}
      restore={(id) => api.settings.updateBusinessUnit(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Code', render: (row) => <span className="font-mono text-xs">{row.code}</span> },
        {
          header: 'Verticals',
          render: (row) =>
            (row.verticalIds ?? []).map((id) => verticalName(id) ?? '—').join(', ') || '—',
        },
        { header: 'Visibility', render: (row) => <StatusBadge status={row.kind} /> },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'code', label: 'Code', required: true },
        {
          name: 'verticalIds',
          label: 'Verticals',
          type: 'multiselect',
          columns: 2,
          required: true,
          options: (verticals?.items ?? []).map((vertical) => ({
            value: vertical.id,
            label: vertical.name,
          })),
          help: 'A shared unit, such as Facilities, can serve more than one vertical.',
        },
        {
          name: 'kind',
          label: 'Visibility',
          type: 'select',
          options: [
            { value: 'STANDARD', label: 'Standard' },
            { value: 'CLASSIFIED', label: 'Classified' },
          ],
          help: 'A classified unit stays hidden unless a user is granted it by name — holding the vertical above it is not enough.',
        },
      ]}
      toFormValues={(row) => ({
        name: row.name,
        code: row.code,
        verticalIds: row.verticalIds ?? [],
        kind: row.kind,
      })}
    />
  );
}

export function LocationsPage() {
  const { companyId } = useAuth();

  const { data: regions } = useQuery({
    queryKey: ['regions', 'for-locations', companyId],
    queryFn: () => api.settings.regions({ companyId, pageSize: 200 }),
  });
  const regionName = (id: string | undefined) =>
    regions?.items.find((region) => region.id === id)?.name;

  return (
    <CrudPage
      title="Locations"
      subtitle="Branches and offices used to filter invoices, payroll and reports"
      queryKey="settings-locations"
      permissions={{
        read: 'location:read',
        create: 'location:create',
        update: 'location:update',
        delete: 'location:delete',
      }}
      list={(query) => api.settings.locations(query)}
      create={(body) => api.settings.createLocation(body)}
      update={(id, body) => api.settings.updateLocation(id, body)}
      schemas={{ create: schemas.createLocationRequest, update: schemas.updateLocationRequest }}
      remove={(id) => api.settings.deleteLocation(id)}
      restore={(id) => api.settings.updateLocation(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Code', render: (row) => <span className="font-mono text-xs">{row.code}</span> },
        { header: 'Region', render: (row) => regionName(row.regionId) ?? '—' },
        { header: 'City', render: (row) => row.city ?? '—' },
        { header: 'State', render: (row) => row.state ?? '—' },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'code', label: 'Code', required: true, help: 'Short code used in payroll files.' },
        {
          name: 'regionId',
          label: 'Region',
          type: 'select',
          required: true,
          options: (regions?.items ?? [])
            .filter((region) => region.active)
            .map((region) => ({ value: region.id, label: region.name })),
          help: 'Every location rolls up into one region.',
        },
        {
          name: 'city',
          label: 'City',
          help: 'One city per location. A second city is a second location.',
        },
        { name: 'state', label: 'State' },
      ]}
      toFormValues={(row) => ({
        name: row.name,
        code: row.code,
        regionId: row.regionId,
        city: row.city ?? '',
        state: row.state ?? '',
      })}
    />
  );
}

/**
 * Departments — PRD §7, §9.
 *
 * The load-bearing field is the head: DEPARTMENT_HEAD approval steps resolve
 * through it, so without this screen the PRD §15 ₹1L–₹10L and >₹10L ladders
 * could not be configured at all without direct API access.
 */
export function DepartmentsPage() {
  const { companyId } = useAuth();

  const { data: users } = useQuery({
    queryKey: ['users', 'for-departments', companyId],
    queryFn: () => api.settings.users({ companyId, pageSize: 200 }),
  });

  const { data: verticals } = useQuery({
    queryKey: ['verticals', 'for-departments', companyId],
    queryFn: () => api.settings.verticals({ companyId, pageSize: 200 }),
  });

  const { data: units } = useQuery({
    queryKey: ['business-units', 'for-departments', companyId],
    queryFn: () => api.settings.businessUnits({ companyId, pageSize: 200 }),
  });

  const userName = (id: string | undefined) => users?.items.find((user) => user.id === id)?.name;
  const verticalName = (id: string | undefined) =>
    verticals?.items.find((vertical) => vertical.id === id)?.name;
  const unitName = (id: string | undefined) => units?.items.find((unit) => unit.id === id)?.name;
  /** The verticals the department's chosen unit serves. */
  const unitVerticals = (values: Record<string, unknown>) =>
    units?.items.find((unit) => unit.id === values.businessUnitId)?.verticalIds ?? [];

  return (
    <CrudPage
      title="Departments"
      subtitle="Used to route approvals and to filter invoices, payroll and reports"
      queryKey="settings-departments"
      permissions={{
        read: 'department:read',
        create: 'department:create',
        update: 'department:update',
        delete: 'department:delete',
      }}
      list={(query) => api.settings.departments(query)}
      create={(body) => api.settings.createDepartment(body)}
      update={(id, body) => api.settings.updateDepartment(id, body)}
      schemas={{ create: schemas.createDepartmentRequest, update: schemas.updateDepartmentRequest }}
      remove={(id) => api.settings.deleteDepartment(id)}
      restore={(id) => api.settings.updateDepartment(id, { active: true })}
      columns={[
        { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
        { header: 'Code', render: (row) => <span className="font-mono text-xs">{row.code}</span> },
        { header: 'Business unit', render: (row) => unitName(row.businessUnitId) ?? '—' },
        { header: 'Vertical', render: (row) => verticalName(row.verticalId) ?? '—' },
        {
          header: 'Head',
          render: (row) =>
            row.headUserId ? (
              (userName(row.headUserId) ?? 'Unknown user')
            ) : (
              <span className="text-amber-700">
                Not set — approvals go to the vertical head, then any approver
              </span>
            ),
        },
        {
          header: 'Status',
          render: (row) => <StatusBadge status={row.active ? 'ACTIVE' : 'INACTIVE'} />,
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'code', label: 'Code', required: true, help: 'Short code used in payroll files.' },
        {
          name: 'businessUnitId',
          label: 'Business unit',
          type: 'select',
          required: true,
          options: (units?.items ?? [])
            .filter((unit) => unit.active)
            .map((unit) => ({
              value: unit.id,
              label: `${unit.name} · ${(unit.verticalIds ?? []).map((id) => verticalName(id) ?? '—').join(', ')}`,
            })),
          help: 'Every department sits in one unit, and in one of the verticals that unit serves.',
        },
        {
          // Only a unit that serves several verticals leaves this to choose.
          name: 'verticalId',
          label: 'Vertical',
          type: 'select',
          required: true,
          visibleWhen: (values) => unitVerticals(values).length > 1,
          optionsFor: (values) =>
            unitVerticals(values).map((id) => ({ value: id, label: verticalName(id) ?? id })),
          help: 'This business unit serves more than one vertical; choose the one this department belongs to.',
        },
        {
          name: 'headUserId',
          label: 'Department head',
          type: 'select',
          options: (users?.items ?? []).map((user) => ({ value: user.id, label: user.name })),
          help: 'Approval rules with a "Department head" step route to this person.',
        },
      ]}
      toFormValues={(row) => ({
        name: row.name,
        code: row.code,
        businessUnitId: row.businessUnitId,
        verticalId: row.verticalId,
        headUserId: row.headUserId ?? '',
      })}
    />
  );
}

interface PossibleDuplicate {
  matches: Array<{ id: string; name: string; code: string; status: string }>;
  confirm(): void;
  cancel(): void;
}

interface LedgerFailure {
  vendorName: string;
  message: string;
}

export function VendorsPage() {
  const queryClient = useQueryClient();
  const [duplicate, setDuplicate] = useState<PossibleDuplicate | null>(null);
  const [ledgerFailure, setLedgerFailure] = useState<LedgerFailure | null>(null);

  const createVendor = async (form: Record<string, unknown>) => {
    const { vendor, ledger } = withoutLedgerAccount(form);
    const created = await createUnlessDuplicate(withOpeningAdvance(vendor));
    if (!ledger) return created;
    const suggested = suggestedVendorAccount(created, ledger.role);
    try {
      await api.settings.createLedger({
        companyId: created.companyId,
        vendorId: created.id,
        partyRole: ledger.role,
        code: ledger.code || suggested.code,
        name: ledger.name || suggested.name,
      });
      void queryClient.invalidateQueries({ queryKey: ['settings-ledgers'] });
    } catch (error) {
      // The vendor is saved, so the form closes: another Save would add it twice.
      setLedgerFailure({ vendorName: created.name, message: describeError(error) });
    }
    return created;
  };

  /**
   * A similar name is only a suspicion, so the server lists the look-alikes
   * and the user decides. The form stays open (and saving) until they do.
   */
  const createUnlessDuplicate = async (body: Record<string, unknown>): Promise<Vendor> => {
    try {
      return await api.settings.createVendor(body);
    } catch (error) {
      const apiError = error as {
        code?: string;
        details?: { matches?: PossibleDuplicate['matches'] };
      };
      if (apiError.code !== 'POSSIBLE_DUPLICATE') throw error;
      return new Promise((resolve, reject) => {
        setDuplicate({
          matches: apiError.details?.matches ?? [],
          confirm: () => {
            setDuplicate(null);
            api.settings.createVendor({ ...body, confirmNotDuplicate: true }).then(resolve, reject);
          },
          cancel: () => {
            setDuplicate(null);
            reject(error);
          },
        });
      });
    }
  };

  return (
    <>
      {duplicate ? <PossibleDuplicateModal duplicate={duplicate} /> : null}
      {ledgerFailure ? (
        <Modal title="Ledger account not added" onClose={() => setLedgerFailure(null)}>
          <p className="text-sm text-slate-600">
            {ledgerFailure.vendorName} was added, but its ledger account was not:{' '}
            {ledgerFailure.message} Add it from the Ledgers button in Edit Vendor.
          </p>
        </Modal>
      ) : null}
      <VendorsCrud create={createVendor} />
    </>
  );
}

function PossibleDuplicateModal({ duplicate }: { duplicate: PossibleDuplicate }) {
  return (
    <Modal
      title="This vendor may already exist"
      onClose={duplicate.cancel}
      footer={
        <>
          <button className="btn-secondary" onClick={duplicate.cancel}>
            Go back
          </button>
          <button className="btn-primary" onClick={duplicate.confirm}>
            Create anyway
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        A vendor with a similar name is already on file. Entering the same vendor twice splits its
        invoices and payments across two records.
      </p>
      <ul className="mt-3 space-y-1 text-sm">
        {duplicate.matches.map((match) => (
          <li key={match.id} className="flex items-center justify-between gap-3">
            <span className="font-medium">{match.name}</span>
            <span className="font-mono text-xs text-slate-500">
              {match.code} · {humanize(match.status)}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

/**
 * The form keeps the opening advance as flat fields in the currency's major
 * unit; the API takes one `openingAdvance` object in minor units.
 */
function withOpeningAdvance(form: Record<string, unknown>): Record<string, unknown> {
  const {
    openingAdvanceAmount,
    openingAdvancePaidOn,
    openingAdvanceMode,
    openingAdvanceReference,
    ...vendor
  } = form;
  if (!openingAdvanceAmount) {
    // A date or reference without the amount is an advance half entered.
    if (openingAdvanceReference || openingAdvancePaidOn) {
      throw {
        status: 422,
        details: [{ path: 'openingAdvanceAmount', message: 'Enter the amount paid, e.g. 50,000' }],
      };
    }
    return vendor;
  }
  const amount = rupeesToMinor(String(openingAdvanceAmount));
  if (!amount) {
    // Shaped like an API refusal, so the form shows it under the field.
    throw {
      status: 422,
      details: [{ path: 'openingAdvanceAmount', message: 'Enter the amount paid, e.g. 50,000' }],
    };
  }
  return {
    ...vendor,
    openingAdvance: {
      amount,
      paidOn: openingAdvancePaidOn,
      mode: openingAdvanceMode,
      reference: openingAdvanceReference,
    },
  };
}

const hasOpeningAdvance = (values: Record<string, unknown>) =>
  String(values.openingAdvanceAmount ?? '').trim() !== '';
/** A date or reference entered for the opening advance: its amount is then required. */
const hasOpeningAdvanceDetail = (values: Record<string, unknown>) =>
  ['openingAdvancePaidOn', 'openingAdvanceReference'].some(
    (field) => String(values[field] ?? '').trim() !== '',
  );

/**
 * The ledger account asked for on the form is created once the vendor exists,
 * so it is checked first: a bad code must not leave a vendor saved without it.
 */
function withoutLedgerAccount(form: Record<string, unknown>) {
  const { vendorLedgerRole, vendorLedgerCode, vendorLedgerName, ...vendor } = form;
  if (!vendorLedgerRole) return { vendor };
  const ledger = {
    role: vendorLedgerRole as LedgerPartyRole,
    code: String(vendorLedgerCode ?? '').trim(),
    name: String(vendorLedgerName ?? '').trim(),
  };
  const { code, name } = schemas.createLedgerRequest.shape;
  const problems = [
    ['vendorLedgerCode', ledger.code ? code.safeParse(ledger.code) : undefined],
    ['vendorLedgerName', ledger.name ? name.safeParse(ledger.name) : undefined],
  ] as const;
  const details = problems.flatMap(([path, result]) =>
    result?.success === false ? [{ path, message: result.error.issues[0]!.message }] : [],
  );
  // Shaped like an API refusal, so the form shows it under the field.
  if (details.length) throw { status: 422, details };
  return { vendor, ledger };
}

const hasLedgerAccount = (values: Record<string, unknown>) => !!values.vendorLedgerRole;

function VendorsCrud({ create }: { create(body: Record<string, unknown>): Promise<unknown> }) {
  const { india, gstin: companyGstin } = useMoney().company;
  const { can, companyId } = useAuth();
  const { ledgers: expenseLedgers } = useExpenseLedgers(companyId, can('ledger:read'));
  const [advanceFor, setAdvanceFor] = useState<Vendor | null>(null);
  const [ledgersFor, setLedgersFor] = useState<Vendor | null>(null);
  // The company's own GSTIN is the buyer's on every invoice it receives, so a
  // vendor saved with it can never be recognised by GSTIN.
  const isCompanyGstin = (value: unknown) =>
    !!companyGstin &&
    typeof value === 'string' &&
    value.trim().toUpperCase() === companyGstin.toUpperCase();
  const canEditVendors = can('vendor:update');
  const advanceAction = (row: Vendor) =>
    can('vendor_advance:read') ? [{ label: 'Advance', run: async () => setAdvanceFor(row) }] : [];
  // The vendor's own accounts in the company's ledgers: Payable, Advance, Deposit…
  const vendorActions = (row: Vendor) => [
    ...advanceAction(row),
    ...(can('ledger:read') ? [{ label: 'Ledgers', run: async () => setLedgersFor(row) }] : []),
  ];
  return (
    <>
      <CrudPage
        title="Vendors"
        subtitle="Payment details used when an approved invoice becomes an obligation"
        queryKey="settings-vendors"
        permissions={{
          read: 'vendor:read',
          create: 'vendor:create',
          update: 'vendor:update',
          delete: 'vendor:delete',
        }}
        list={(query) => api.settings.vendors(query)}
        create={create}
        update={(id, body) => api.settings.updateVendor(id, body)}
        schemas={{ create: schemas.createVendorRequest, update: schemas.updateVendorRequest }}
        remove={(id) => api.settings.deleteVendor(id)}
        restore={(id) => api.settings.updateVendor(id, { status: 'ACTIVE' })}
        columns={[
          { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
          {
            header: 'Code',
            render: (row) => <span className="font-mono text-xs">{row.code}</span>,
          },
          { header: 'Email', render: (row) => row.email ?? '—' },
          {
            header: india ? 'GSTIN' : 'Tax ID',
            render: (row) => (
              <span className="font-mono text-xs">
                {(india ? row.gstin : row.taxId) ?? '—'}
                {india && isCompanyGstin(row.gstin) ? (
                  <span
                    className="badge badge-attention ml-1.5 rounded-md px-1.5 font-sans"
                    title="This is the company's own GSTIN, not the vendor's"
                  >
                    Same as company
                  </span>
                ) : null}
              </span>
            ),
          },
          {
            header: 'Bank details',
            render: (row) => {
              // India pays on account + IFSC; elsewhere on account/IBAN + SWIFT or routing code.
              const account = row.bankAccountNumber || row.iban;
              const code = india ? row.ifsc : row.swiftBic || row.routingCode;
              return account && code ? (
                <span className="font-mono text-xs">
                  …{account.slice(-4)} · {code}
                </span>
              ) : (
                <span className="text-amber-700">Missing — cannot be paid</span>
              );
            },
          },
          {
            header: 'TDS',
            render: (row) => {
              const profiles = row.tdsProfiles ?? [];
              if (!profiles.length) return <span className="text-slate-400">None</span>;
              const primary = profiles.find((profile) => profile.isDefault) ?? profiles[0]!;
              const rest = profiles.length - 1;
              return (
                <span>
                  {primary.label} ({primary.section} · {formatBasisPoints(primary.rateBasisPoints)})
                  {rest > 0 ? ` +${rest} more` : ''}
                </span>
              );
            },
          },
          { header: 'Terms', render: (row) => `${row.paymentTermsDays} days` },
          ...(can('vendor_advance:read')
            ? [
                {
                  header: 'Advance',
                  align: 'right' as const,
                  render: (row: Vendor) =>
                    row.advanceBalance ? (
                      <Money minor={row.advanceBalance} />
                    ) : (
                      <span className="text-ink-400">—</span>
                    ),
                },
              ]
            : []),
          {
            header: 'Status',
            render: (row) => (
              <StatusBadge status={row.status === 'BLOCKED' ? 'INACTIVE' : row.status} />
            ),
          },
        ]}
        // A row opens the vendor's edit form, which carries the Advance button.
        // Whoever may see advances but not edit vendors keeps it on the row.
        formActions={vendorActions}
        rowActions={canEditVendors ? undefined : vendorActions}
        fields={[
          { name: 'name', label: 'Vendor name', required: true },
          { name: 'code', label: 'Vendor code', help: 'Generated automatically if left blank.' },
          {
            name: 'email',
            label: 'Email',
            type: 'email',
            help: 'Payment confirmations are sent here.',
          },
          { name: 'phone', label: 'Phone' },
          // An Indian company's vendors carry GSTIN, PAN, TDS and an IFSC; a
          // company elsewhere pays its local vendors by IBAN/SWIFT or a routing code.
          ...(india
            ? ([
                {
                  name: 'gstin',
                  label: 'GSTIN',
                  help: 'A vendor’s identity: two vendors in one company cannot share a GSTIN.',
                  warningFor: (values) =>
                    isCompanyGstin(values.gstin)
                      ? 'This is this company’s own GSTIN — the buyer’s, not the vendor’s. Enter the GSTIN printed on the vendor’s invoices, or their invoices cannot be matched to this vendor.'
                      : undefined,
                },
                {
                  name: 'pan',
                  label: 'PAN',
                  help: 'Required before TDS can be deducted. Unique within the company, like GSTIN.',
                },
                {
                  name: 'tdsProfiles',
                  label: 'TDS profiles',
                  type: 'tdsProfiles',
                  help: 'A vendor billing more than one kind of work — e.g. professional fees and rent — can have a profile for each. Defaults only: the accounting team confirms or overrides on each invoice.',
                },
                {
                  name: 'rcm',
                  label: 'RCM (reverse charge) by default',
                  type: 'checkbox',
                  help: 'Its invoices start with RCM ticked: the GST on them is paid by the company. Review and accounting can untick it on any one.',
                },
              ] satisfies FieldDefinition[])
            : ([
                {
                  name: 'taxId',
                  label: 'Tax ID',
                  help: 'The vendor’s VAT, TRN or other tax registration. Used to recognise its invoices.',
                },
              ] satisfies FieldDefinition[])),
          {
            name: 'beneficiaryName',
            label: 'Beneficiary name',
            help: 'If it differs from the vendor name.',
          },
          { name: 'bankAccountNumber', label: 'Bank account number' },
          ...(india
            ? ([{ name: 'ifsc', label: 'IFSC' }] satisfies FieldDefinition[])
            : ([
                {
                  name: 'iban',
                  label: 'IBAN',
                  help: 'Instead of, or as well as, the account number.',
                },
                { name: 'swiftBic', label: 'SWIFT / BIC' },
                {
                  name: 'routingCode',
                  label: 'Local routing code',
                  help: 'Sort code, ABA or BSB, where the bank pays locally without SWIFT.',
                },
                { name: 'bankName', label: 'Bank name' },
              ] satisfies FieldDefinition[])),
          { name: 'paymentTermsDays', label: 'Payment terms (days)', type: 'number' },
          { name: 'notes', label: 'Notes' },
          // Recorded as the vendor's first deposit. Later top-ups go through the
          // Advance button in the vendor's edit form.
          ...(can('vendor_advance:create')
            ? ([
                {
                  name: 'openingAdvanceAmount',
                  label: 'Opening Balance/Advance amount',
                  createOnly: true,
                  // Required as soon as an advance is being recorded.
                  requiredWhen: hasOpeningAdvanceDetail,
                  help: 'Already paid to the vendor in advance, if any. Leave blank for none.',
                },
                {
                  name: 'openingAdvancePaidOn',
                  label: 'Advance paid on',
                  type: 'date',
                  createOnly: true,
                  requiredWhen: hasOpeningAdvance,
                },
                {
                  name: 'openingAdvanceMode',
                  label: 'Payment mode',
                  type: 'select',
                  createOnly: true,
                  requiredWhen: hasOpeningAdvance,
                  options: VENDOR_ADVANCE_MODES.map((mode) => ({
                    value: mode,
                    label: humanize(mode),
                  })),
                },
                {
                  name: 'openingAdvanceReference',
                  label: 'UTR / cheque no.',
                  createOnly: true,
                  requiredWhen: hasOpeningAdvance,
                },
              ] satisfies FieldDefinition[])
            : []),
          // Later changes to either go through the Ledgers button in the
          // vendor's edit form.
          ...(can('ledger:read')
            ? ([
                {
                  name: 'defaultExpenseLedgerId',
                  label: 'Default expense ledger',
                  type: 'select',
                  createOnly: true,
                  options: expenseLedgers.map((ledger) => ({
                    value: ledger.id,
                    label: `${ledger.code} · ${ledger.name}`,
                  })),
                  help: 'What this vendor’s invoices are usually for. It pre-fills each invoice; accounting confirms or changes it at verification.',
                },
              ] satisfies FieldDefinition[])
            : []),
          ...(can('ledger:create')
            ? ([
                {
                  name: 'vendorLedgerRole',
                  label: 'Add a ledger account',
                  type: 'select',
                  createOnly: true,
                  options: LEDGER_PARTY_ROLES.map((role) => ({
                    value: role,
                    label: humanize(role),
                  })),
                  help: 'The vendor’s Payable account is created for you. Add another here if it needs one, e.g. Advance.',
                },
                {
                  name: 'vendorLedgerCode',
                  label: 'Account code',
                  createOnly: true,
                  help: 'Leave blank for one based on the vendor’s code, e.g. DAIKIN-ADV.',
                  visibleWhen: hasLedgerAccount,
                },
                {
                  name: 'vendorLedgerName',
                  label: 'Account name',
                  createOnly: true,
                  help: 'Leave blank for one based on the vendor’s name, e.g. Daikin – Advance.',
                  visibleWhen: hasLedgerAccount,
                },
              ] satisfies FieldDefinition[])
            : []),
        ]}
        defaults={{ paymentTermsDays: 30, openingAdvanceMode: 'NEFT', rcm: false }}
        formColumns={2}
      />
      {/* After the list, so it opens above the edit form it was opened from. */}
      {advanceFor ? (
        <VendorAdvancePanel vendor={advanceFor} onClose={() => setAdvanceFor(null)} />
      ) : null}
      {ledgersFor ? (
        <VendorLedgersPanel vendor={ledgersFor} onClose={() => setLedgersFor(null)} />
      ) : null}
    </>
  );
}

export function UsersPage() {
  const { companyId, can } = useAuth();
  const [invite, setInvite] = useState<{ email: string; url: string } | null>(null);

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.settings.companies({ pageSize: 100 }),
  });

  // The catalogue rather than the built-in list, so roles the tenant created
  // under Settings → Roles can be granted here too.
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.settings.roles(),
  });
  const roleLabels = Object.fromEntries((roles?.items ?? []).map((role) => [role.key, role.label]));

  // Every company's units, labelled with their company when there is more
  // than one, so a scope can be granted across the companies a user holds.
  const scope = useScopeOptions(companies?.items ?? []);

  // A Vendor-role login is linked to vendor rows, one per company the vendor
  // bills; its companies follow from them, so it gets this picker instead of
  // the company and scope fields.
  const { data: vendors } = useQuery({
    queryKey: ['vendors', 'for-users'],
    queryFn: () => api.settings.vendors({ pageSize: 200 }),
    enabled: can('vendor:read'),
  });
  const companyName = (id: string) =>
    companies?.items.find((company) => company.id === id)?.name ?? '';
  const isVendorLogin = (values: Record<string, unknown>) => values.roleKey === RoleKey.VENDOR;
  const isStaffLogin = (values: Record<string, unknown>) => !isVendorLogin(values);

  return (
    <>
      <CrudPage
        title="Users"
        subtitle="Who can sign in, and what each of them may do"
        queryKey="settings-users"
        permissions={{
          read: 'user:read',
          create: 'user:create',
          update: 'user:update',
          delete: 'user:delete',
        }}
        list={(query) => api.settings.users(query)}
        create={async (body) => {
          const created = await api.settings.createUser(body);
          // An account created without a password cannot sign in until this
          // link is used, so it is surfaced immediately rather than lost.
          if (created.inviteUrl) {
            setInvite({ email: created.email, url: created.inviteUrl });
          }
          return created;
        }}
        update={(id, body) => api.settings.updateUser(id, body)}
        schemas={{ create: schemas.createUserRequest, update: schemas.updateUserRequest }}
        remove={(id) => api.settings.deleteUser(id)}
        restore={(id) => api.settings.updateUser(id, { status: 'ACTIVE' })}
        // In the user's edit form: resending needs the same permission as editing.
        formActions={(row) =>
          row.status === 'INVITED'
            ? [
                {
                  label: 'Resend invite',
                  run: async () => {
                    const result = await api.settings.reinviteUser(row.id);
                    setInvite({ email: row.email, url: result.inviteUrl });
                  },
                },
              ]
            : []
        }
        columns={[
          { header: 'Name', render: (row) => <span className="font-medium">{row.name}</span> },
          { header: 'Email', render: (row) => row.email },
          {
            header: 'Role',
            render: (row) => roleLabel(row.roleKey, roleLabels),
          },
          {
            header: 'Companies',
            render: (row) =>
              row.companyIds.length === 0
                ? 'All'
                : row.companyIds
                    .map((id) => companies?.items.find((company) => company.id === id)?.name ?? '—')
                    .join(', '),
          },
          {
            header: 'Scope',
            // A vendor login is scoped by its vendor links, not the org axes,
            // so "Unrestricted" would be the opposite of the truth.
            render: (row) => {
              if (row.roleKey !== RoleKey.VENDOR) return scope.summarise(row);
              const links = row.vendorIds?.length ?? 0;
              return `Own invoices · ${links} vendor link${links === 1 ? '' : 's'}`;
            },
          },
          { header: 'Last login', render: (row) => formatDate(row.lastLoginAt) },
          {
            header: 'Status',
            render: (row) => (
              <span className="flex items-center gap-2">
                <StatusBadge status={row.status} />
                {row.status === 'INVITED' ? (
                  <span className="text-xs text-slate-500">has not signed in yet</span>
                ) : null}
              </span>
            ),
          },
        ]}
        fields={[
          { name: 'name', label: 'Full name', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true },
          {
            name: 'password',
            label: 'Password',
            help: 'Leave blank to create the account as invited with a generated password.',
          },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INVITED', label: 'Invited — cannot sign in yet' },
              { value: 'SUSPENDED', label: 'Suspended' },
            ],
            help: 'Only an active account can sign in.',
          },
          {
            name: 'roleKey',
            label: 'Role',
            type: 'select',
            required: true,
            options: (roles?.items ?? [])
              .filter((role) => role.active)
              .map((role) => ({
                value: role.key,
                // The same count the Roles screen shows, so the weight of a
                // role is visible at the moment it is granted.
                label: `${role.label} (${role.permissionCount} permissions)`,
              })),
            help: 'One role per user. A combination of duties is a role of its own — create it under Roles.',
          },
          {
            name: 'vendorIds',
            label: 'Vendors',
            type: 'multiselect',
            columns: 2,
            required: true,
            options: (vendors?.items ?? []).map((vendor) => ({
              value: vendor.id,
              label: `${vendor.name} — ${companyName(vendor.companyId)}`,
            })),
            help: 'Pick this vendor in every company it bills. The login sees only these vendors’ invoices, and only in their companies.',
            visibleWhen: isVendorLogin,
          },
          {
            name: 'companyIds',
            label: 'Companies',
            visibleWhen: isStaffLogin,
            type: 'multiselect',
            columns: 2,
            options: (companies?.items ?? []).map((company) => ({
              value: company.id,
              label: company.name,
            })),
            help: 'Leave empty for access to every company in the tenant.',
          },
          ...scope.fields.map((field) => ({ ...field, visibleWhen: isStaffLogin })),
        ]}
        formColumns={2}
        defaults={{ companyIds: companyId ? [companyId] : [] }}
        toFormValues={(row) => ({
          name: row.name,
          email: row.email,
          roleKey: row.roleKey,
          companyIds: row.companyIds,
          vendorIds: row.vendorIds ?? [],
          ...Object.fromEntries(SCOPE_AXES.map((axis) => [axis.field, row[axis.field]])),
          status: row.status,
        })}
      />
      {/* After the list, so a resent link opens above the edit form. */}
      {invite ? <InviteLinkModal invite={invite} onClose={() => setInvite(null)} /> : null}
    </>
  );
}

/** The org axes below company a user's access can be narrowed on. */
const SCOPE_AXES = [
  { field: 'groupIds', label: 'Groups', tenantWide: true },
  { field: 'regionIds', label: 'Regions' },
  { field: 'verticalIds', label: 'Verticals' },
  { field: 'businessUnitIds', label: 'Business units' },
  { field: 'locationIds', label: 'Locations' },
  { field: 'departmentIds', label: 'Departments' },
] as const;

type ScopeField = (typeof SCOPE_AXES)[number]['field'];

/**
 * Options and a one-line summary for the scope axes on the Users screen.
 *
 * Access is the role's permissions narrowed by this scope: an empty axis is
 * unrestricted, a filled one limits the user to exactly those units.
 */
function useScopeOptions(companies: Array<{ id: string; name: string }>) {
  const query = { pageSize: 200 };
  const lists = {
    groupIds: useQuery({
      queryKey: ['scope', 'groups'],
      queryFn: () => api.settings.groups(query),
    }),
    regionIds: useQuery({
      queryKey: ['scope', 'regions'],
      queryFn: () => api.settings.regions(query),
    }),
    verticalIds: useQuery({
      queryKey: ['scope', 'verticals'],
      queryFn: () => api.settings.verticals(query),
    }),
    businessUnitIds: useQuery({
      queryKey: ['scope', 'business-units'],
      queryFn: () => api.settings.businessUnits(query),
    }),
    locationIds: useQuery({
      queryKey: ['scope', 'locations'],
      queryFn: () => api.settings.locations(query),
    }),
    departmentIds: useQuery({
      queryKey: ['scope', 'departments'],
      queryFn: () => api.settings.departments(query),
    }),
  };

  const companyName = (id: string | undefined) =>
    companies.find((company) => company.id === id)?.name;
  const labelFor = (item: { name: string; companyId?: string }) =>
    companies.length > 1 && item.companyId
      ? `${item.name} · ${companyName(item.companyId) ?? '—'}`
      : item.name;

  const items = (field: ScopeField) =>
    (lists[field].data?.items ?? []) as Array<{ id: string; name: string; companyId?: string }>;

  const fields: FieldDefinition[] = SCOPE_AXES.map((axis) => ({
    name: axis.field,
    label: `${axis.label} (scope)`,
    type: 'multiselect',
    columns: 2,
    options: items(axis.field).map((item) => ({ value: item.id, label: labelFor(item) })),
    help: `Leave empty for every ${axis.label.toLowerCase()} the role can reach.`,
  }));

  const summarise = (row: Record<ScopeField, string[]>) => {
    const parts = SCOPE_AXES.flatMap((axis) => {
      const ids = row[axis.field] ?? [];
      if (!ids.length) return [];
      const names = ids.map((id) => items(axis.field).find((item) => item.id === id)?.name ?? '—');
      return [names.join(', ')];
    });
    return parts.length ? parts.join(' · ') : <span className="text-slate-400">Unrestricted</span>;
  };

  return { fields, summarise };
}

/**
 * Shows an invitation link once, right after it is issued.
 *
 * The token is never retrievable again — only its hash is stored — so this is
 * the single opportunity to hand it over.
 */
function InviteLinkModal({
  invite,
  onClose,
}: {
  invite: { email: string; url: string };
  onClose(): void;
}) {
  const link = `${window.location.origin}${invite.url}`;
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      title="Invitation created"
      onClose={onClose}
      footer={
        <button className="btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <p className="text-sm text-slate-600">
        Send this link to <span className="font-medium">{invite.email}</span>. They will set their
        own password and the account becomes active. It expires in seven days.
      </p>
      <p className="mt-3 break-all rounded-md bg-slate-50 p-3 font-mono text-xs">{link}</p>
      <button
        className="btn-secondary mt-3"
        onClick={() => {
          void navigator.clipboard?.writeText(link).then(() => setCopied(true));
        }}
      >
        {copied ? 'Copied' : 'Copy link'}
      </button>
      <p className="mt-3 text-xs text-slate-500">
        This link is shown only once. If it is lost, use “Resend invite” on the user row.
      </p>
    </Modal>
  );
}

export function BankAccountsPage({
  extraColumns = [],
  rowActions,
  formFooter,
  createFooter,
  onCreated,
}: {
  /** Columns after the account's own, e.g. the cards and logins it carries. */
  extraColumns?: Array<CrudColumn<BankAccount>>;
  rowActions?(row: BankAccount): CrudRowAction[];
  /** Shown under the account's edit form, e.g. its cards and net banking logins. */
  formFooter?(row: BankAccount): ReactNode;
  /** Shown under the form that adds an account, e.g. the cards to add with it. */
  createFooter?: ReactNode;
  /** Runs once a new account is saved, before its form closes. */
  onCreated?(account: BankAccount): Promise<void>;
} = {}) {
  const { company } = useMoney();
  return (
    <CrudPage<BankAccount>
      embedded
      title="Bank Accounts"
      subtitle="Accounts payments are made from, and statements imported into"
      queryKey="settings-bank-accounts"
      permissions={{
        read: 'bank_account:read',
        create: 'bank_account:create',
        update: 'bank_account:update',
        delete: 'bank_account:delete',
      }}
      list={(query) => api.settings.bankAccounts(query)}
      create={async (body) => {
        const created = await api.settings.createBankAccount(body);
        await onCreated?.(created);
        return created;
      }}
      update={(id, body) => api.settings.updateBankAccount(id, body)}
      schemas={{
        create: schemas.createBankAccountRequest,
        update: schemas.updateBankAccountRequest,
      }}
      remove={(id) => api.settings.deleteBankAccount(id)}
      restore={(id) => api.settings.updateBankAccount(id, { active: true })}
      columns={[
        // A long label wraps, and the bank and its codes share a column, so the
        // row still fits a laptop screen once a page adds its own columns. Every
        // account holds the company's currency, shown on the balance.
        {
          header: 'Label',
          render: (row) => (
            <span className="block min-w-[8rem] max-w-[10rem] whitespace-normal font-medium">
              {row.label}
            </span>
          ),
        },
        {
          header: 'Bank',
          render: (row) => (
            <>
              <span className="block">{row.bankName}</span>
              <span className="block font-mono text-xs text-ink-500">
                …{row.accountNumber.slice(-4)} · {(company.india ? row.ifsc : row.swiftBic) ?? '—'}
              </span>
            </>
          ),
        },
        { header: 'File format', render: (row) => humanize(row.bankFileFormat) },
        {
          header: 'Balance',
          align: 'right',
          render: (row) => <Money minor={row.currentBalance} currency={row.currency} />,
        },
        ...extraColumns,
      ]}
      rowActions={rowActions}
      formFooter={formFooter}
      createFooter={createFooter}
      formColumns={formFooter ? 2 : undefined}
      fields={[
        { name: 'label', label: 'Label', required: true },
        { name: 'bankName', label: 'Bank name', required: true },
        { name: 'accountNumber', label: 'Account number', required: true },
        // The currency follows the company; India needs the IFSC, elsewhere the SWIFT.
        company.india
          ? { name: 'ifsc', label: 'IFSC', required: true }
          : { name: 'swiftBic', label: 'SWIFT / BIC' },
        {
          name: 'bankFileFormat',
          label: 'Bank file format',
          type: 'select',
          // HDFC and ICICI files carry rupee (NEFT/RTGS) payments only.
          options: [
            ...(company.india
              ? [
                  { value: 'HDFC', label: 'HDFC bulk payment' },
                  { value: 'ICICI', label: 'ICICI corporate payment' },
                ]
              : []),
            { value: 'GENERIC_XLSX', label: 'Generic Excel' },
            { value: 'GENERIC_CSV', label: 'Generic CSV' },
          ],
          help: 'Determines the layout of the file generated for this account.',
        },
      ]}
      defaults={{ bankFileFormat: 'GENERIC_XLSX' }}
    />
  );
}
